import type { Activity, HistoryFilters } from "@/types/activity";
import { defineStore } from "pinia";

interface HistoryState {
  history: Activity[];
  currentPageNumber: number;
  totalHistoryCount: number;
  filters: HistoryFilters;
  filtersVersion: number;
  triggerReload: boolean;
  lastLoadedType: number | null;
  isError: boolean;
  isSyncing: boolean;
  pendingActivityUniqueId: string;
  syncRetryCount: number;
  maxSyncRetries: number;
  isDisabled: boolean;
  isApplyingHistory: boolean;
}

export const useHistoryStore = defineStore("history", {
  state: (): HistoryState => ({
    history: [],
    currentPageNumber: 1,
    totalHistoryCount: -1,
    filters: {},
    filtersVersion: 0,
    triggerReload: false,
    lastLoadedType: null,
    isError: false,
    isSyncing: false,
    pendingActivityUniqueId: "",
    syncRetryCount: 0,
    maxSyncRetries: 5,
    isDisabled: false,
    isApplyingHistory: false
  }),

  actions: {
    appendHistory(items: Activity[]) {
      this.history.push(...items);
      this.history.sort((a, b) => {
        const t1 = new Date(
          a.logTime + (a.logTime.endsWith("Z") ? "" : "Z")
        ).getTime();

        const t2 = new Date(
          b.logTime + (b.logTime.endsWith("Z") ? "" : "Z")
        ).getTime();

        return t2 - t1;
      });
    },

    setFilters(filters: HistoryFilters) {
      this.filters = { ...this.filters, ...filters };
      this.resetResults();
    },

    clearFilters() {
      this.filters = {};
      this.resetResults();
    },

    // Clears the loaded history but keeps the filters, so the list reloads with them.
    resetResults() {
      this.history = [];
      this.currentPageNumber = 1;
      this.totalHistoryCount = -1;
      this.isError = false;
      this.filtersVersion++;
    },

    setTriggerReload(value: boolean) {
      this.triggerReload = value;
    },

    setLastLoadedType(type: number) {
      this.lastLoadedType = type;
    },
    setIsError(value: boolean) {
      this.isError = value;
    },
    setSyncRetryCount(count: number) {
      this.syncRetryCount = count;
    },
    incrementSyncRetry() {
      this.syncRetryCount++;
    },
    setPendingActivityUniqueId(id: string) {
      this.pendingActivityUniqueId = id;
    },
    setIsSyncing(value: boolean) {
      this.isSyncing = value;
    },
    setIsDisabled(value: boolean) {
      this.isDisabled = value;
    },
    setIsApplyingHistory(value: boolean){
      this.isApplyingHistory = value;
    },
    reset() {
      this.history = [];
      this.currentPageNumber = 1;
      this.totalHistoryCount = -1;
      this.filters = {};
      this.triggerReload = false;
      this.lastLoadedType = null;
      this.isError = false;
      this.isDisabled = false;
      this.isApplyingHistory = false;
    },
  },
});
